import { actLocation, actTitle, entryLabel, isNow, isPast, timeOrFolgt } from '@/lib/actHelpers';
import { useVotes } from '@/hooks/useVotes';
import type { Activity } from '@/api/types';
import { FavToggle } from './FavToggle';
import { StarRating } from './StarRating';
import { VoteButton } from './VoteButton';
import styles from './ActCard.module.css';

interface ActCardProps {
  slug: string;
  act: Activity;
}

const MAX_VOTER_NAMES = 4;

/**
 * Sundown-Act-Card: Time-Spalte links (mono, big), Body mit Title + Meta +
 * Pills + Voter-Liste, Fav-Star rechts oben.
 * "Uhrzeit folgt" -> Time-Cell wird kompakter und kriegt einen "TBA"-Hint.
 */
export function ActCard({ slug, act }: ActCardProps) {
  const { votes } = useVotes(slug);
  const voters = votes[act.id] ?? [];
  const running = isNow(act);
  const past = isPast(act);

  const entry = entryLabel(act);
  const location = actLocation(act);
  const title = actTitle(act);
  const hasTime = !!act.time?.trim();
  const time = timeOrFolgt(act);

  const voterNames = voters.slice(0, MAX_VOTER_NAMES).map((v) => v.name).join(', ');
  const extraVoters = voters.length - MAX_VOTER_NAMES;

  const className = [styles.card, running ? styles.now : '', past ? styles.past : '']
    .filter(Boolean)
    .join(' ');

  return (
    <article className={className}>
      <div className={styles.time}>
        {hasTime ? time : <span>—</span>}
        {!hasTime ? <span className={styles.tba}>Folgt</span> : null}
      </div>
      <div className={styles.body}>
        <div className={styles.head}>
          <div className={styles.titleWrap}>
            <h3 className={styles.title}>{title}</h3>
            {location ? <div className={styles.location}>{location}</div> : null}
          </div>
          <FavToggle slug={slug} actId={act.id} />
        </div>
        {act.note ? <p className={styles.note}>{act.note}</p> : null}
        {(running || act.type === 'meetup' || entry) ? (
          <div className={styles.pills}>
            {running ? <span className={`${styles.pill} ${styles.pillNow}`}>🔴 Läuft</span> : null}
            {act.type === 'meetup'
              ? <span className={`${styles.pill} ${styles.pillMeetup}`}>Treffpunkt</span>
              : null}
            {entry ? <span className={styles.pill}>{entry}</span> : null}
          </div>
        ) : null}
        <div className={styles.actions}>
          <VoteButton slug={slug} actId={act.id} />
          {voters.length > 0 ? (
            <span className={styles.voters}>
              <span className={styles.voterNames}>{voterNames}</span>
              {extraVoters > 0 ? ` +${extraVoters}` : ''}
            </span>
          ) : null}
        </div>
        {(running || past) && act.type !== 'meetup' ? (
          <StarRating slug={slug} actId={act.id} />
        ) : null}
      </div>
    </article>
  );
}
